import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight, CheckCircle, Zap, Shield, Plane } from "lucide-react";
import * as Icons from "lucide-react";
import { MarketingNav, MarketingFooter } from "./MarketingLayout";
import { MODULES, MODULE_COLOR } from "./moduleData";
import "../../marketing.css";

const INTEGRATIONS = [
  "DGCA SDMS", "Jeppesen AIMS", "AMOS / Ramco MRO", "ACARS Live Feed",
  "AviationStack", "SMS Safety Reporting", "Custom API Webhooks",
];

const TECH_PILLARS = [
  { label: "React PWA", desc: "Mobile-first. Works offline. No app store install." },
  { label: "FastAPI Backend", desc: "Python async — real-time updates in <50ms." },
  { label: "Claude Sonnet AI", desc: "Departure confidence scoring & crew recommendations." },
  { label: "MongoDB Atlas", desc: "Schema-flexible. Scales from 5 to 500 rotations/day." },
];

export default function MarketingPlatform() {
  return (
    <div className="min-h-screen bg-[#07111f] text-white marketing-font-body">
      <MarketingNav />

      {/* ── HERO ── */}
      <section className="pt-28 pb-16 relative overflow-hidden grid-texture">
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 80% 60% at 60% 40%, rgba(249,115,22,0.06) 0%, transparent 70%)" }} />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 px-3 py-1.5 rounded mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-500 live-dot" />
              <span className="text-[10px] text-orange-400 marketing-font-mono uppercase font-bold tracking-widest">Platform Overview</span>
            </div>
            <h1 className="text-4xl lg:text-[3.2rem] font-black marketing-font-heading leading-tight mb-5">
              One platform.<br />
              <span className="text-orange-400">End-to-end ground operations.</span>
            </h1>
            <p className="text-lg text-slate-400 leading-relaxed mb-8 max-w-2xl">
              Fatigue Shield + GroundShield gives you crew FRMS compliance, real-time ramp execution, AI delay prediction and vendor SLA accountability — in a single platform built for Indian SME airlines.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/marketing/demo"
                className="flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-bold px-7 py-3.5 transition-all hover:-translate-y-0.5 shadow-lg shadow-orange-500/20"
                data-testid="platform-demo-btn">
                Request Demo <ArrowRight size={16} />
              </Link>
              <Link to="/login"
                className="flex items-center gap-2 border border-white/15 hover:border-white/30 text-slate-300 hover:text-white px-7 py-3.5 transition-all">
                Sign In <ChevronRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── ALL 7 MODULES ── */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-12">
            <div className="text-[10px] text-orange-400 marketing-font-mono uppercase tracking-widest font-bold mb-3">The full platform</div>
            <h2 className="text-3xl lg:text-4xl font-black marketing-font-heading">7 modules. Two brands. One data layer.</h2>
          </div>

          <div className="space-y-4">
            {MODULES.map((m, i) => {
              const c = MODULE_COLOR[m.color] || MODULE_COLOR.orange;
              const Icon = Icons[m.icon] || Icons.Plane;
              return (
                <Link key={m.slug} to={`/marketing/module/${m.slug}`}
                  className={`group flex items-center gap-6 bg-[#0c1929]/50 border ${c.border} hover:border-opacity-60 p-5 lg:p-6 card-glow transition-all fade-in-up fade-in-up-${(i % 4) + 1}`}
                  data-testid={`platform-module-${m.slug}`}>
                  <div className={`w-10 h-10 ${c.bg} border ${c.border} flex items-center justify-center shrink-0`}>
                    <Icon size={18} className={c.text} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-[9px] marketing-font-mono uppercase tracking-widest font-bold mb-1 ${c.text}`}>{m.badge} · {m.brand}</div>
                    <div className="text-sm font-bold text-white marketing-font-heading group-hover:text-orange-100 transition-colors">{m.name}</div>
                    <div className="text-xs text-slate-600 mt-0.5 line-clamp-1">{m.tagline}</div>
                  </div>
                  <div className="hidden lg:flex items-center gap-2 shrink-0">
                    {m.roi.slice(0, 2).map((r, ri) => (
                      <div key={ri} className={`${c.bg} border ${c.border} px-3 py-1.5 text-center`}>
                        <div className={`text-sm font-black marketing-font-mono ${c.text}`}>{r.metric}</div>
                        <div className="text-[8px] text-slate-600 marketing-font-mono uppercase tracking-wider">{r.label.slice(0, 18)}</div>
                      </div>
                    ))}
                  </div>
                  <ChevronRight size={16} className="text-slate-600 group-hover:text-white transition-colors shrink-0" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── TECH STACK ── */}
      <section className="py-20 border-t border-white/5 grid-texture">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-12">
            <div className="text-[10px] text-orange-400 marketing-font-mono uppercase tracking-widest font-bold mb-3">Built for scale</div>
            <h2 className="text-3xl font-black marketing-font-heading">Modern stack. Production-ready.</h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
            {TECH_PILLARS.map((t, i) => (
              <div key={i} className={`bg-[#0c1929]/60 border border-white/5 hover:border-orange-500/20 p-6 card-glow fade-in-up fade-in-up-${i + 1}`}>
                <div className="text-sm font-bold text-white marketing-font-mono mb-2">{t.label}</div>
                <div className="text-xs text-slate-500 leading-relaxed">{t.desc}</div>
              </div>
            ))}
          </div>

          {/* Integrations */}
          <div>
            <div className="text-[10px] text-slate-500 marketing-font-mono uppercase tracking-widest font-bold mb-5">Works with your existing systems</div>
            <div className="flex flex-wrap gap-2">
              {INTEGRATIONS.map((int, i) => (
                <span key={i} className="text-xs text-slate-500 border border-white/8 px-3 py-1.5 marketing-font-mono hover:border-orange-500/30 hover:text-slate-300 transition-colors">
                  {int}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── COMPLIANCE ── */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-[10px] text-orange-400 marketing-font-mono uppercase tracking-widest font-bold mb-4">Regulatory Compliance</div>
              <h2 className="text-3xl font-black marketing-font-heading mb-6">
                DGCA Phase 1, 2 & 3<br />
                <span className="text-orange-400">built into every module.</span>
              </h2>
              <p className="text-slate-400 leading-relaxed mb-8">
                Every pairing, roster and duty period is validated against DGCA FDTL 2020 rules in real-time. The FRMS module tracks fatigue scientifically, not just legally — because compliance without safety is just paperwork.
              </p>
              <div className="space-y-3">
                {[
                  "FDP / FTL limits enforced at pairing build time",
                  "Crew rest compliance auto-checked before assignment",
                  "DGCA SDMS occurrence report submission built-in",
                  "FRMS Phase 3 hazard register and audit trail",
                  "Ground staff fatigue scoring — IATA ISAGO aligned",
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-slate-400">
                    <CheckCircle size={13} className="text-green-400 shrink-0" />
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              {[
                { phase: "Phase 1", label: "FDTL Monitoring", desc: "Fatigue score tracking, crew status board, Do Not Assign alerts.", color: "text-orange-400", bg: "bg-orange-500/5 border-orange-500/15" },
                { phase: "Phase 2", label: "Predictive Risk Engine", desc: "AI bio-mathematical fatigue modelling, 7-day pattern detection.", color: "text-amber-400", bg: "bg-amber-500/5 border-amber-500/15" },
                { phase: "Phase 3", label: "Full FRMS", desc: "Policy library, hazard register, scientific evidence, DGCA audits.", color: "text-green-400", bg: "bg-green-500/5 border-green-500/15" },
              ].map((p, i) => (
                <div key={i} className={`border ${p.bg} p-5`}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`text-[9px] marketing-font-mono font-black uppercase tracking-widest ${p.color} bg-current/10 px-2 py-1`}>{p.phase}</div>
                    <div className="font-bold text-white text-sm">{p.label}</div>
                  </div>
                  <div className="text-xs text-slate-500 leading-relaxed">{p.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 border-t border-white/5 bg-[#04090f]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-3xl lg:text-4xl font-black marketing-font-heading mb-5">
            Ready to see it running?
          </h2>
          <p className="text-slate-400 max-w-lg mx-auto mb-8">
            30-minute live demo with data configured for your airline. No commitment, no sales deck.
          </p>
          <Link to="/marketing/demo"
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-bold px-9 py-4 text-base transition-all hover:-translate-y-0.5 shadow-xl shadow-orange-500/20">
            Request Demo <ArrowRight size={17} />
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
